import * as React from "react";
import { Button } from "@jhool-io/fe-components";
import FolderOpenIcon from "../../../../components/Icons/FolderOpen";
import { ConsentAttachmentFieldConfig } from "../../types/client-forms.types";
import { cn } from "../../../../utils/helpers";

interface ConsentAttachmentsStepProps {
    fields: ConsentAttachmentFieldConfig[];
    uploadedFieldIds: Set<string>;
    initialUploadedFiles?: Record<string, string[]>;
    pendingFiles: Record<string, File[]>;
    onPendingFilesChange: (fieldId: string, files: File[]) => void;
    fieldError?: string;
    onInteract?: () => void;
}

const fileKey = (file: File) =>
    `${file.name}-${file.size}-${file.lastModified}`;

export default function ConsentAttachmentsStep({
    fields,
    uploadedFieldIds,
    initialUploadedFiles,
    pendingFiles,
    onPendingFilesChange,
    fieldError,
    onInteract,
}: ConsentAttachmentsStepProps) {
    const [draggedFieldId, setDraggedFieldId] = React.useState<string | null>(
        null
    );
    const [fieldErrors, setFieldErrors] = React.useState<
        Record<string, string>
    >({});
    const hasRequiredFields = fields.some((field) => field.required);

    const handleFiles = (
        fieldId: string,
        maxFiles: number,
        selectedFiles: File[]
    ) => {
        if (!selectedFiles.length) return;

        onInteract?.();
        const uploadedCount = initialUploadedFiles?.[fieldId]?.length ?? 0;
        const currentFiles = pendingFiles[fieldId] ?? [];
        const remaining = Math.max(
            0,
            maxFiles - uploadedCount - currentFiles.length
        );
        const existingKeys = new Set(currentFiles.map(fileKey));
        const filesToAdd = selectedFiles
            .filter((file) => !existingKeys.has(fileKey(file)))
            .slice(0, remaining);
        const fileLabel = maxFiles > 1 ? "files" : "file";

        if (!filesToAdd.length) {
            setFieldErrors((previous) => ({
                ...previous,
                [fieldId]: `Maximum of ${maxFiles} ${fileLabel} selected.`,
            }));
            return;
        }

        setFieldErrors((previous) => ({ ...previous, [fieldId]: "" }));
        onPendingFilesChange(fieldId, [...currentFiles, ...filesToAdd]);
    };

    const handleFileChange = (
        fieldId: string,
        maxFiles: number,
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const input = event.target;
        handleFiles(fieldId, maxFiles, Array.from(input.files ?? []));
        input.value = "";
    };

    const handleDrop = (
        fieldId: string,
        maxFiles: number,
        event: React.DragEvent<HTMLLabelElement>
    ) => {
        event.preventDefault();
        setDraggedFieldId(null);
        handleFiles(fieldId, maxFiles, Array.from(event.dataTransfer.files));
    };

    const handleRemove = (fieldId: string, fileToRemove: File) => {
        onInteract?.();
        onPendingFilesChange(
            fieldId,
            (pendingFiles[fieldId] ?? []).filter(
                (file) => fileKey(file) !== fileKey(fileToRemove)
            )
        );
    };

    return (
        <div className="space-y-20">
            <div className="pb-8 border-b border-strokelight">
                <h2 className="text-sm font-semibold text-[#0B132B] mb-8">
                    Document Uploads
                </h2>
                {hasRequiredFields && (
                    <p className="text-xs text-[rgba(11,19,43,0.6)]">
                        All fields marked with{" "}
                        <span className="text-red-500">*</span> are required.
                    </p>
                )}
            </div>

            {fields.map((field) => {
                const uploadedFiles =
                    initialUploadedFiles?.[field.fieldId] ?? [];
                const selectedFiles = pendingFiles[field.fieldId] ?? [];
                const isUploaded = uploadedFieldIds.has(field.fieldId);
                const maxFiles = field.maxFiles ?? 1;
                const isFull =
                    uploadedFiles.length + selectedFiles.length >= maxFiles;

                return (
                    <div key={field.fieldId} className="space-y-6">
                        <p className="text-sm font-medium text-[#0B132B]">
                            {field.required && (
                                <span className="text-red-500 mr-4">*</span>
                            )}
                            {field.required
                                ? `Required upload: ${field.label}`
                                : `Optional: ${field.label}`}
                        </p>

                        {!isFull && (
                            <label
                                htmlFor={`attachment-${field.fieldId}`}
                                className={cn(
                                    "flex w-full items-center gap-16 rounded-lg border",
                                    "border-dashed border-primary px-16 py-12",
                                    "cursor-pointer transition-colors",
                                    "focus-within:ring-2 focus-within:ring-primary",
                                    "focus-within:ring-offset-2",
                                    {
                                        "border-2 border-stroke bg-bg-stroke":
                                            draggedFieldId === field.fieldId,
                                    }
                                )}
                                onDragEnter={() =>
                                    setDraggedFieldId(field.fieldId)
                                }
                                onDragLeave={() => setDraggedFieldId(null)}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={(event) =>
                                    handleDrop(field.fieldId, maxFiles, event)
                                }
                            >
                                <input
                                    type="file"
                                    id={`attachment-${field.fieldId}`}
                                    className="sr-only"
                                    aria-label={`Click to upload ${field.label}`}
                                    multiple={maxFiles > 1}
                                    onChange={(event) =>
                                        handleFileChange(
                                            field.fieldId,
                                            maxFiles,
                                            event
                                        )
                                    }
                                />
                                <span
                                    className={cn(
                                        "shrink-0 rounded-md bg-primary-50",
                                        "p-16 text-primary"
                                    )}
                                >
                                    <FolderOpenIcon />
                                </span>
                                <span className="text-sm text-black">
                                    <span className="font-semibold text-primary">
                                        Click to upload documents
                                    </span>
                                    {" or drag your file here"}
                                    <span className="mt-4 block text-xs font-normal text-graydark">
                                        Up to {maxFiles} file
                                        {maxFiles > 1 ? "s" : ""}
                                    </span>
                                </span>
                            </label>
                        )}

                        <p className="text-xs text-[rgba(11,19,43,0.6)]">
                            {field.description}
                        </p>

                        {uploadedFiles.map((name) => (
                            <div
                                key={name}
                                className={cn(
                                    "flex w-full items-center justify-between",
                                    "rounded-md border border-stroke px-16 py-12"
                                )}
                            >
                                <span className="min-w-0 truncate text-xs text-graydark">
                                    {name}
                                </span>
                                <span className="ml-12 shrink-0 text-xs text-green-600">
                                    Uploaded
                                </span>
                            </div>
                        ))}

                        {selectedFiles.map((file) => (
                            <div
                                key={fileKey(file)}
                                className={cn(
                                    "flex w-full items-center justify-between",
                                    "rounded-md border border-stroke px-16 py-12"
                                )}
                            >
                                <span className="min-w-0 truncate text-xs text-graydark">
                                    {file.name}
                                </span>
                                <Button
                                    variant="normal"
                                    className="ml-12 shrink-0 text-xs font-medium text-red-600"
                                    onClick={() =>
                                        handleRemove(field.fieldId, file)
                                    }
                                >
                                    Remove
                                </Button>
                            </div>
                        ))}

                        {isUploaded && uploadedFiles.length === 0 && (
                            <p className="text-xs text-green-600">
                                ✓ File uploaded
                            </p>
                        )}

                        {fieldErrors[field.fieldId] && (
                            <p className="text-xs text-red-500">
                                {fieldErrors[field.fieldId]}
                            </p>
                        )}
                    </div>
                );
            })}

            {fieldError && (
                <p className="text-xs text-red-500 mt-8">{fieldError}</p>
            )}
        </div>
    );
}
